import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import Combobox from '@helios/Combobox';
import { ADDRESS_PRIMARY_TYPES, getStructuredAddress, getUnitLine, loadGooglePlacesLibrary } from '@/utils/googlePlaces.js';
const MIN_QUERY_LENGTH = 3;
export const QUERY_DELAY_MS = 250;
const MAX_SUGGESTIONS = 6;
const STRUCTURED_ADDRESS_FIELDS = ['address', 'city', 'state', 'zip', 'country'];

// The server has no layout phase; in the browser the ref follows each commit.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** The field's status copy (Combobox `messages`). */
export const ADDRESS_MESSAGES = {
  loading: 'Loading address suggestions.',
  results: count => `${count} address ${count === 1 ? 'suggestion' : 'suggestions'} available. Use the up and down arrow keys to review them, then press Enter to select an address.`,
  idle: 'You may enter the address manually.'
};
function getPredictionLabel(suggestion) {
  return suggestion?.placePrediction?.text?.toString?.() ?? '';
}

/**
 * The application's street address (Street Address 1) with Google Places
 * suggestions, on Helios Combobox. Google stays here and in
 * `utils/googlePlaces.js`: the script loads after the third character, the
 * session token spans a lookup and its selection, and the selected place
 * fills the address fields in Formik (a unit moves to line 2, O450). Without
 * a key, or when Google fails, the field is a plain input with nothing to
 * suggest. A FieldGroup around it passes `aria-describedby` (its error) and
 * `aria-invalid`, which Combobox merges with its status text.
 */
function AddressAutocompleteField({
  id = 'address',
  loadPlaces = loadGooglePlacesLibrary,
  'aria-describedby': describedBy = undefined,
  'aria-invalid': ariaInvalid = undefined
}) {
  const formik = useFormikContext();
  // The latest committed Formik state: a selection reads it after awaiting
  // the place's details, so an edit made meanwhile is not lost. Updated in
  // the commit itself (a layout effect): a passive effect can lag a
  // non-urgent commit, and the selection's continuation could run between.
  const formikRef = useRef(formik);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef(null);
  const skipNextRequestRef = useRef(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const address = formik.values.address ?? '';
  const invalid = Boolean(formik.touched.address && formik.errors.address);
  useIsomorphicLayoutEffect(() => {
    formikRef.current = formik;
  });
  useEffect(() => {
    const query = address.trim();
    if (skipNextRequestRef.current) {
      skipNextRequestRef.current = false;
      return undefined;
    }
    if (query.length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1;
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const places = await loadPlaces();
        if (requestId !== requestIdRef.current || !places?.AutocompleteSuggestion || !places?.AutocompleteSessionToken) {
          return;
        }
        sessionTokenRef.current ??= new places.AutocompleteSessionToken();
        const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
          includedRegionCodes: ['us'],
          language: 'en-US',
          region: 'us',
          sessionToken: sessionTokenRef.current
        });
        if (requestId !== requestIdRef.current) return;
        setSuggestions((response?.suggestions ?? []).filter(suggestion => suggestion.placePrediction).slice(0, MAX_SUGGESTIONS));
      } catch {
        // Google is an enhancement. A failure must leave manual entry intact.
        if (requestId === requestIdRef.current) setSuggestions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, QUERY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [address, loadPlaces]);
  const handleSelect = async suggestion_0 => {
    const placePrediction = suggestion_0.placePrediction;
    if (!placePrediction) return;
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents']
      });
      const current = formikRef.current;
      const selected = getStructuredAddress(place);
      // O450: a unit typed on the street line, or the place's own, moves to
      // line 2 when the applicant left it empty; the street becomes the
      // structured street.
      const unit = selected.address ? getUnitLine(current.values.address, selected.secondAddress) : '';
      const fillUnit = Boolean(unit) && !current.values.secondAddress?.trim();
      const nextValues = {
        ...current.values,
        address: selected.address || getPredictionLabel(suggestion_0) || current.values.address,
        ...(fillUnit && {
          secondAddress: unit
        }),
        city: selected.city || current.values.city,
        state: selected.state || current.values.state,
        zip: selected.zip || current.values.zip,
        country: selected.country || current.values.country || 'US'
      };
      const filledFields = fillUnit ? [...STRUCTURED_ADDRESS_FIELDS, 'secondAddress'] : STRUCTURED_ADDRESS_FIELDS;
      const nextTouched = {
        ...current.touched
      };
      filledFields.forEach(field => {
        nextTouched[field] = false;
      });
      skipNextRequestRef.current = true;
      requestIdRef.current += 1;
      await current.setValues(nextValues, false);
      await current.setTouched(nextTouched, false);
      await current.validateForm(nextValues);
      setSuggestions([]);
      sessionTokenRef.current = null;
    } catch {
      // Keep the typed address and all manual controls usable on detail
      // errors; Combobox has closed the list.
    }
  };
  return <Combobox id={id} name="address" placeholder="Start typing your street address" autoComplete="address-line1" listLabel="Address suggestions" options={suggestions} getOptionLabel={getPredictionLabel} onSelect={suggestion_1 => void handleSelect(suggestion_1)} loading={loading} messages={ADDRESS_MESSAGES} value={address} onChange={event => {
    const nextAddress = event.currentTarget.value;
    if (nextAddress.trim().length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1;
      setSuggestions([]);
      setLoading(false);
    }
    void formik.setFieldValue('address', nextAddress, false);
  }} onBlur={() => void formik.setFieldTouched('address', true, false)} aria-describedby={describedBy} aria-invalid={ariaInvalid ?? (invalid || undefined)} />;
}
export default AddressAutocompleteField;